/**
 * เหมันต์สะกดธาร (the snow-sword warp chain, fx 'snowblink'): each warp leaves frost afterimages and a
 * streak of snow, strikes its own cut in step with the blade with a frost crescent, frost spreads
 * on the ground and the first cut on each foe freezes it in ice (shattering when it breaks free);
 * the finisher (or the chain broken off) ends in a flourish of frost with the screen icing over.
 * The ice sheets themselves are drawn in iceFx.ts.
 */
import type { Entity, SkillDef } from '@nine/shared';
import { drawSheet, sizeBucket } from '../fxSheets';
import { frostedFrame, ICE } from '../iceFx';
import { artH, artW, artX, artY } from '../pixel';
import { SKILL_SWINGS, type SwingStyle } from '../skillFx';
import { FRAMES, viewOf } from '../sprites';
import { registerFx, type FxHost } from './registry';

const R = Math.random;

/** เหมันต์สะกดธาร's cuts after its first, in turn (the first is `snowOpen`, the last `snowFinale`). */
const SNOW_CUTS: readonly SwingStyle[] = ['snowRise', 'snowCleave', 'snowThrust', 'snowSweep'];
/** Frost afterimages left along each warp. */
const SNOW_GHOSTS = 3;
/** Particles the snow and mist add no more of once this many are flying. */
const SNOW_PARTS = 700;

/** A เหมันต์สะกดธาร chain under way, by caster: what the client knows of it between warps. */
interface SnowChain {
  /** Where it began (world), the reach it chains within, the cuts it may make and the warp gap. */
  ox: number;
  oy: number;
  reach: number;
  hits: number;
  gap: number;
  /** Warps so far, when the last came, and on whom. */
  n: number;
  at: number;
  tgtId: number;
  /** The last warp was the finisher; the chain is over (finished or broken off). */
  last: boolean;
  over: boolean;
  /** When the finisher's flourish goes off (world time), once. */
  flourishAt: number;
  /** Mist owed to the caster this frame (fractions carried over). */
  mist: number;
}

registerFx('snowblink', (host: FxHost) => {
  /** เหมันต์สะกดธาร chains under way, by caster. */
  const snow = new Map<number, SnowChain>();
  /**
   * The ice frozen foes stand in, by foe, until their stun ends (then it shatters): `t` seconds
   * since it formed (negative until the blade lands), grown over the first moment, cracking over
   * `dur`, the time the foe stays frozen. Drawn with the foe (`drawHeld`), so whoever stands in
   * front of it is drawn in front of the ice.
   */
  const ice = new Map<number, { t: number; dur: number; size: number }>();

  /**
   * Frost laid on the ground at (x, y) (screen), `r` art pixels across at most, for `life` seconds,
   * after `delay`; drawn under the figures. A few at a time at most.
   */
  const ground = (x: number, y: number, kind: 'frost', r: number, life: number, delay = 0): void => {
    let n = 0;
    for (const f of host.fx) if (f.type === 'sheet' && f.ground) n++;
    if (n >= 12) return;
    // Radii in steps of four, so near sizes share their drawn frames.
    const R = Math.max(8, Math.round(r / 4) * 4);
    host.fx.push({
      type: 'sheet',
      x,
      y,
      t: -delay,
      life,
      s: { kind, tint: 'ice', size: 1, r: R },
      ground: true,
    });
  };

  /** Snowflakes drifting down from (x, y), out after `delay`. */
  const flakes = (x: number, y: number, n: number, spread: number, delay = 0): void => {
    for (let k = 0; k < n && host.parts.length < SNOW_PARTS; k++)
      host.parts.push({
        x: x + (R() - 0.5) * spread,
        y: y + (R() - 0.5) * spread * 0.6,
        vx: (R() - 0.5) * 26,
        vy: -12 - R() * 16,
        g: 30,
        t: -delay,
        life: 0.9 + R() * 0.7,
        c: R() < 0.6 ? ICE.white : ICE.pale,
        s: R() < 0.3 ? 2 : 1,
        drag: 2.2,
      });
  };

  /**
   * One warp: frost afterimages and a streak of snow from where the caster vanished to where it
   * reappears, the cut it strikes there (a different one each warp: the first drawn from the low
   * stance with frost gathering on the blade, the last the finisher) and its frost crescent, in step
   * with the blade.
   */
  const warp = (e: Entity, tgt: Entity | null, fromX: number, fromY: number, s: SkillDef): void => {
    const w = host.world;
    let st = snow.get(e.id);
    if (!st) {
      st = {
        ox: fromX,
        oy: fromY,
        reach: s.aoe ?? s.range,
        hits: s.hits ?? 1,
        gap: s.blink ?? 0.4,
        n: 0,
        at: w.time,
        tgtId: -1,
        last: false,
        over: false,
        flourishAt: -1,
        mist: 0,
      };
      snow.set(e.id, st);
    }
    const n = st.n++;
    if (!n) {
      // Frost left on the ground where the caster stood.
      const [gx, gy] = host.iso.proj(fromX, fromY);
      ground(gx, gy, 'frost', 30, 1.6);
    }
    st.at = w.time;
    st.tgtId = tgt?.id ?? -1;
    st.over = false;
    // The last cut: the chain's cuts are spent, or nobody is left in its reach to warp to.
    const { ox, oy, reach } = st;
    const foeLeft = [...w.entities.values()].some(
      (o) => !o.dead && o !== e && w.isHostile(e, o) && Math.hypot(o.x - ox, o.y - oy) <= reach,
    );
    st.last = n > 0 && (n + 1 >= st.hits || !foeLeft);
    const style: SwingStyle =
      n === 0 ? 'snowOpen' : st.last ? 'snowFinale' : SNOW_CUTS[(n - 1) % SNOW_CUTS.length]!;
    host.startSwing(e, style, false);
    const A = host.anim(e.id);
    // Seconds until the blade meets the foe.
    const contact = SKILL_SWINGS[style]!.contact * A.swingDur;
    const [x0, y0] = host.iso.proj(fromX, fromY);
    const [x1, y1] = host.pos(e);
    const H = host.figH(e);
    const f = host.iso.facing(e);
    // Gone from where it stood in a puff of snow, leaving frost afterimages of the launch along the
    // way, and a streak of snow.
    host.snowBurst(x0, y0 - H * 0.4, 6, false);
    const set = host.sprites.of(e);
    if (set && Math.hypot(x1 - x0, y1 - y0) > 6) {
      const { set: view, facing } = viewOf(set, host.iso.heading(e), f);
      const stand = () => view.poses.dash ?? view.walk[1]!;
      const img = view.extra?.('snowVanish', 0, false, stand) ?? stand();
      for (let k = 0; k < SNOW_GHOSTS; k++) {
        const q = k / SNOW_GHOSTS;
        host.fx.push({
          type: 'custom',
          x: x0 + (x1 - x0) * q,
          y: y0 + (y1 - y0) * q - e.z,
          t: -k * 0.03,
          life: 0.28 - k * 0.05,
          draw: (g, x, y, k) => drawFrostGhost(g, img, facing < 0, view.ax, view.ay, x, y, k),
        });
      }
      const trail = { x1, y1: y1 - H * 0.35, seed: Math.floor(R() * 999) };
      host.fx.push({
        type: 'custom',
        x: x0,
        y: y0 - H * 0.35,
        t: 0,
        life: 0.3,
        draw: (g, x, y, k, ox, oy) => drawFrostTrail(g, x, y, trail.x1 + ox, trail.y1 + oy, trail.seed, k),
      });
    }
    // Set down on a breath of frost.
    ground(x1, y1, 'frost', 18 * e.scale, 0.9);
    if (style === 'snowOpen')
      // The draw-stance: frost drawn onto the blade held low behind, then a glint off it.
      host.fx.push({
        type: 'sheet',
        x: x1 - f * H * 0.22,
        y: y1 - H * 0.2 - e.z,
        t: 0,
        life: contact,
        s: { kind: 'frostGlint', tint: 'ice', size: e.scale },
      });
    const finale = style === 'snowFinale';
    host.fx.push({
      type: 'sheet',
      x: x1 + f * 3 * e.scale,
      y: y1 - H * (style === 'snowSweep' || style === 'snowOpen' ? 0.42 : 0.5) - e.z,
      t: -Math.max(0, contact - 0.04),
      life: finale ? 0.5 : 0.34,
      s: { kind: 'iceArc', tint: 'ice', size: e.scale * (finale ? 1.25 : 1), flip: f < 0, arc: style },
    });
    if (finale) st.flourishAt = w.time + contact;
    host.shake = Math.max(host.shake, 2);
    host.sfx.dash();
    if (e.kind === 'player') host.cinematic(e, 1, 0.55, true);
  };

  /** The ice round a foe breaking (its stun over, or the foe struck down). */
  const shatterIce = (e: Entity, size: number): void => {
    const [x, y] = host.pos(e);
    host.fx.push({
      type: 'sheet',
      x,
      y: y - e.z,
      t: 0,
      life: 0.55,
      s: { kind: 'iceShatter', tint: 'ice', size },
    });
    flakes(x, y - host.figH(e) * 0.5, 8, 24);
    host.sfx.ching();
  };

  /**
   * The finisher landing (or the chain broken off): an X of frost across the last foe, its crystals
   * bursting, frost flooding the ground, snow, the screen flashing cold with frost creeping in.
   */
  const flourish = (e: Entity, tgt: Entity | null): void => {
    const at = tgt && !tgt.dead ? tgt : (tgt ?? e);
    const [x, y] = host.pos(at);
    const cy = y - host.figH(at) * 0.5 - at.z;
    for (const flip of [false, true])
      host.fx.push({
        type: 'sheet',
        x,
        y: cy,
        t: flip ? -0.05 : 0,
        life: 0.55,
        s: { kind: 'iceArc', tint: 'ice', size: 1.25, flip, arc: 'snowFinale' },
      });
    host.fx.push({
      type: 'sheet',
      x,
      y: cy,
      t: 0.02,
      life: 0.6,
      s: { kind: 'iceShards', tint: 'ice', size: 1.75 },
    });
    ground(x, y, 'frost', 46, 2.2);
    flakes(x, cy, 18, 50);
    host.snowBurst(x, cy, 12, true);
    host.frost = 1;
    host.impact('214,242,255', 0.45, 1, 8);
    host.sfx.nine();
  };

  return {
    // The warps themselves are drawn per 'blink' event, the first of which has already set the
    // caster down by its first foe: a new chain starts its count afresh.
    skill: ({ src }) => void snow.delete(src.id),

    blink: warp,

    /**
     * A cut landing on `tgt` (at screen x, y; chest height cy): ice crystals bursting out of it,
     * snow, frost spreading under it, and — the first cut on each foe freezes it stiff — the ice it
     * is held in. All in step with the blade, which meets it a moment after the warp.
     */
    hit: ({ src, tgt, x, y, cy, landed }) => {
      const A = host.anim(src.id);
      const sw = SKILL_SWINGS[A.style];
      const delay = sw ? Math.max(0, A.swingAt + sw.contact * A.swingDur - host.world.time) : 0;
      host.fx.push({
        type: 'sheet',
        x,
        y: cy,
        t: -delay,
        life: 0.5,
        s: { kind: 'iceShards', tint: 'ice', size: tgt.scale },
      });
      flakes(x, cy, 6, 20, delay);
      ground(x, y, 'frost', 24 * tgt.scale, 1.6, delay);
      if (landed && tgt.stunT > 0 && !tgt.dead && !ice.has(tgt.id))
        ice.set(tgt.id, {
          t: -delay,
          dur: tgt.stunT,
          size: Math.max(0.5, sizeBucket(host.figH(tgt) / 64)),
        });
      host.sfx.ching();
    },

    /**
     * Between events: cold mist curling off the caster, the finisher's flourish when its blade
     * lands, the blade flicked clean when the chain breaks off before its finisher, and the ice
     * round frozen foes shattering when they break free.
     */
    step: (dt) => {
      const w = host.world;
      for (const [id, st] of snow) {
        const e = w.get(id);
        if (!e || e.dead) {
          snow.delete(id);
          continue;
        }
        const since = w.time - st.at;
        if (since < st.gap + 0.9) {
          const [x, y] = host.pos(e);
          const H = host.figH(e);
          for (st.mist += dt * 36; st.mist >= 1; st.mist--) {
            if (host.parts.length >= SNOW_PARTS) continue;
            host.parts.push({
              x: x + (R() - 0.5) * H * 0.35,
              y: y - e.z - R() * H * 0.75,
              vx: (R() - 0.5) * 14,
              vy: -5 - R() * 9,
              g: -10,
              t: 0,
              life: 0.6 + R() * 0.5,
              c: R() < 0.45 ? ICE.pale : R() < 0.6 ? ICE.cyan : ICE.white,
              s: R() < 0.35 ? 2 : 1,
              drag: 1.5,
            });
          }
        }
        if (st.flourishAt >= 0 && w.time >= st.flourishAt) {
          st.flourishAt = -1;
          flourish(e, w.get(st.tgtId) ?? null);
        }
        if (!st.over && st.n > 0 && since > st.gap + 0.06) {
          st.over = true;
          if (!st.last) {
            if (!e.moving) host.startSwing(e, 'snowFinish', false);
            flourish(e, w.get(st.tgtId) ?? null);
          }
        }
        if (since > st.gap + 1.6) snow.delete(id);
      }
      for (const [id, c] of ice) {
        c.t += dt;
        if (c.t < 0) continue;
        const e = w.get(id);
        if (e && !e.dead && e.stunT > 0 && c.t < 30) continue;
        ice.delete(id);
        if (e) shatterIce(e, c.size);
      }
    },

    /**
     * The ice a frozen foe stands in, drawn right after the foe (renderer.ts) at its feet (`x`, `y`,
     * where the foe itself was drawn, shaking with it): forming over its first moment, then
     * cracking toward the moment the foe breaks free, over `FRAMES` frames.
     */
    drawHeld: (g, e, x, y) => {
      const c = ice.get(e.id);
      if (!c || c.t < 0) return;
      const form = 0.15;
      const p =
        c.t < form
          ? (c.t / form) * 0.2
          : 0.2 + 0.8 * Math.min(1, (c.t - form) / Math.max(0.05, c.dur - form));
      drawSheet(g, { kind: 'iceShell', tint: 'ice', size: c.size }, p, x, y);
    },
  };
});

/** A frost afterimage: a figure's frame turned to ice (iceFx.ts), fading out in `FRAMES` steps. */
function drawFrostGhost(
  g: CanvasRenderingContext2D,
  frame: HTMLCanvasElement,
  flip: boolean,
  ax: number,
  ay: number,
  x: number,
  y: number,
  k: number,
): void {
  const img = frostedFrame(frame);
  g.globalAlpha = 0.55 * (1 - Math.floor(k * FRAMES) / FRAMES);
  const rx = Math.round(x);
  const ry = Math.round(y);
  if (flip) {
    g.save();
    g.translate(rx, 0);
    g.scale(-1, 1);
    g.drawImage(img, artX(img) - ax, ry - ay + artY(img), artW(img), artH(img));
    g.restore();
  } else g.drawImage(img, rx - ax + artX(img), ry - ay + artY(img), artW(img), artH(img));
  g.globalAlpha = 1;
}

/**
 * A streak of snow along a warp, from (x0, y0) to (x1, y1) on screen: hard pixels in the ice tones,
 * a white core over a deep blue edge with flakes shed either side, draining from where the warp
 * began in `FRAMES` steps.
 */
function drawFrostTrail(
  g: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  seed: number,
  k: number,
): void {
  const step = Math.floor(k * FRAMES) / FRAMES;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.max(1, Math.ceil(Math.hypot(dx, dy)));
  const nx = -dy / len;
  const ny = dx / len;
  for (let i = 0; i <= len; i++) {
    const u = i / len;
    if (u < step) continue;
    // Thickest toward where the caster reappeared.
    const w = (u - step) / (1 - step || 1);
    const px = Math.round(x0 + dx * u);
    const py = Math.round(y0 + dy * u);
    g.fillStyle = ICE.deep;
    g.fillRect(px, py + 1, 1, 1);
    g.fillStyle = w > 0.35 ? ICE.white : ICE.pale;
    g.fillRect(px, py, 1, 1);
    if (w > 0.6) {
      g.fillStyle = ICE.cyan;
      g.fillRect(px, py - 1, 1, 1);
    }
    // Flakes shed off the streak.
    const h = Math.sin((seed + i) * 12.9898) * 43758.5453;
    const r = h - Math.floor(h);
    if (i % 3 === 0 && r > 0.4) {
      const off = (r - 0.7) * 10 * (0.5 + w);
      g.fillStyle = r > 0.8 ? ICE.white : ICE.cyan;
      g.fillRect(Math.round(px + nx * off), Math.round(py + ny * off), r > 0.9 ? 2 : 1, r > 0.9 ? 2 : 1);
    }
  }
}
